/**
 * A recording overlay: the engine and input state, burned into the picture.
 *
 * Bug reports about this shell arrive as a screen recording or a phone photo
 * of a tablet, and the interesting failures — a long-press that opened the
 * wrong menu, a resize that stuck, a click landing a few game pixels off — are
 * invisible in the frame. Everything the shell knows at that instant is
 * therefore printed over the canvas, in a font that survives video
 * compression, so the picture itself is the diagnostic.
 *
 * This ships in production. It is off unless asked for (`?telemetry=1`, or the ` key),
 * costs one text write per frame when on, and — because it must never change
 * what it is measuring — never takes pointer events, never calls
 * preventDefault, and never moves anything else in the shell.
 */

import type { EmModule } from '../engine';
import type { DisplayMode, GameType } from '../config';

const STORAGE_KEY = 'rr-telemetry';
const LOG_KEEP = 200;
const LOG_SHOW = 8;
const EVENTS_SHOW = 6;

export interface TelemetryOptions {
  /** The game canvas. Pointer coordinates are reported relative to it. */
  canvas: HTMLCanvasElement;
}

export interface EngineInfo {
  gameType?: GameType;
  module?: EmModule | null;
  mode?: DisplayMode | null;
}

export interface Telemetry {
  /** Mirror one engine stdout/stderr line into the overlay's ring buffer. */
  engineLog(line: string): void;
  /** Tell the overlay what booted. Safe to call more than once. */
  setEngine(info: EngineInfo): void;
  /** Whether the panel is currently showing. */
  isOn(): boolean;
  /** Show/hide without the hotkey. */
  toggle(on?: boolean): void;
}

interface RecentEvent {
  t: number;
  text: string;
}

function readFlag(): boolean {
  try {
    const param = new URLSearchParams(location.search).get('telemetry');
    if (param === '1' || param === 'on') return true;
    if (param === '0' || param === 'off') return false;
  } catch {
    /* malformed query string is not a reason to fail */
  }
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function persist(on: boolean): void {
  try {
    if (on) localStorage.setItem(STORAGE_KEY, '1');
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode; the URL parameter still works */
  }
}

function clock(): string {
  const d = new Date();
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

function describeElement(el: Element | null): string {
  if (!el) return 'none';
  const tag = el.tagName.toLowerCase();
  return el.id ? `${tag}#${el.id}` : tag;
}

export function mountTelemetry(opts: TelemetryOptions): Telemetry {
  const session = Math.random().toString(36).slice(2, 6).toUpperCase();
  const logLines: string[] = [];
  let logRevision = 0;
  let paintedLogRevision = -1;

  let engineInfo: EngineInfo = {};
  let on = false;
  let root: HTMLElement | null = null;
  let mainEl: HTMLElement | null = null;
  let logEl: HTMLElement | null = null;
  let raf = 0;
  let flashUntil = 0;
  let flashText = '';

  // Input state. Kept even while the panel is hidden costs nothing, so it is
  // instead only tracked while on — the listeners are the cost, not the state.
  const held = new Set<string>();
  const recent: RecentEvent[] = [];
  let pointerType = '-';
  let clientX = 0;
  let clientY = 0;
  let buttons = 0;
  let buttonDownAt = 0;

  // A cached canvas box. Reading it per pointer event would force layout on
  // every mousemove, which is exactly the kind of interference this must not
  // introduce; it is refreshed on a timer and on resize instead.
  let rect = { left: 0, top: 0, width: 0, height: 0 };
  let rectAt = 0;

  function refreshRect(now: number): void {
    if (now - rectAt < 250) return;
    const r = opts.canvas.getBoundingClientRect();
    rect = { left: r.left, top: r.top, width: r.width, height: r.height };
    rectAt = now;
  }

  function note(text: string): void {
    recent.push({ t: performance.now(), text });
    if (recent.length > EVENTS_SHOW) recent.shift();
  }

  const onKeyDown = (e: KeyboardEvent): void => {
    if (!e.repeat) {
      held.add(e.code || e.key);
      note(`kd ${e.code || e.key}`);
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    // Both spellings: a synthetic or remapped release can arrive with a
    // different `code` than the press, and a key stuck in the held set for the
    // rest of the session is worse than a missing one.
    held.delete(e.code);
    held.delete(e.key);
    note(`ku ${e.code || e.key}`);
  };
  const onBlur = (): void => held.clear();

  const onPointer = (e: PointerEvent | MouseEvent): void => {
    pointerType = e.type;
    clientX = e.clientX;
    clientY = e.clientY;
    const previous = buttons;
    buttons = e.buttons;
    if (previous === 0 && buttons !== 0) buttonDownAt = performance.now();
    if (buttons === 0) buttonDownAt = 0;
    if (e.type !== 'pointermove' && e.type !== 'mousemove') note(`${e.type} b=${e.buttons}`);
  };

  /** Body of the panel, minus the engine log. One string, written once. */
  function compose(now: number): string {
    const canvas = opts.canvas;
    const dw = canvas.width;
    const dh = canvas.height;
    const cw = rect.width;
    const ch = rect.height;
    const relX = clientX - rect.left;
    const relY = clientY - rect.top;
    const gameX = cw > 0 ? relX * dw / cw : 0;
    const gameY = ch > 0 ? relY * dh / ch : 0;
    const holdMs = buttonDownAt ? now - buttonDownAt : 0;
    const mode = engineInfo.mode;
    const resize = engineInfo.module?._rr_resize_status?.();

    const lines = [
      'RR TELEMETRY  ` hide  ctrl+shift+Y copy',
      `SESSION ${session}  ${clock()}  ${fps.toFixed(0)}fps`,
      `game ${engineInfo.gameType ?? '-'}  engine ${engineInfo.module ? 'up' : 'not booted'}` +
        (resize === undefined ? '' : `  resize=${resize}`),
      `buf ${dw}x${dh}  css ${cw.toFixed(0)}x${ch.toFixed(0)}  dpr ${devicePixelRatio.toFixed(2)}`,
      `mode ${mode ? `${mode.id} ${mode.width}x${mode.height}` : '-'}`,
      `lock ${describeElement(document.pointerLockElement)}  focus ${describeElement(document.activeElement)}`,
      `ptr ${pointerType} c ${clientX.toFixed(0)},${clientY.toFixed(0)}` +
        `  rel ${relX.toFixed(0)},${relY.toFixed(0)}  game ${gameX.toFixed(0)},${gameY.toFixed(0)}`,
      `btn ${buttons}${holdMs ? `  held ${holdMs.toFixed(0)}ms` : ''}`,
      `keys ${held.size ? [...held].slice(0, 6).join(' ') : '-'}`,
      ...recent
        .slice()
        .reverse()
        .map((ev) => `  ${(ev.t / 1000).toFixed(1)}s ${ev.text}`),
    ];
    if (now < flashUntil) lines.splice(1, 0, flashText);
    return lines.join('\n');
  }

  function paintLog(): void {
    if (!logEl || paintedLogRevision === logRevision) return;
    paintedLogRevision = logRevision;
    logEl.textContent = '';
    for (const line of logLines.slice(-LOG_SHOW)) {
      const row = document.createElement('div');
      row.className = line.startsWith('rr_') ? 'rr-telemetry-log-line is-rr' : 'rr-telemetry-log-line';
      row.textContent = line;
      logEl.appendChild(row);
    }
  }

  // Rolling one-second frame rate.
  let fps = 0;
  let frames = 0;
  let fpsWindowAt = 0;

  function tick(now: number): void {
    if (!on) return;
    raf = requestAnimationFrame(tick);
    frames++;
    if (now - fpsWindowAt >= 1000) {
      fps = frames * 1000 / (now - fpsWindowAt);
      frames = 0;
      fpsWindowAt = now;
    }
    refreshRect(now);
    if (mainEl) mainEl.textContent = compose(now);
    paintLog();
  }

  function panelText(): string {
    return `${mainEl?.textContent ?? ''}\n${logLines.slice(-LOG_SHOW).join('\n')}`;
  }

  function copy(): void {
    const text = [
      panelText(),
      '',
      `--- engine log (last ${Math.min(LOG_KEEP, logLines.length)}) ---`,
      ...logLines.slice(-LOG_KEEP),
    ].join('\n');
    const flash = (message: string) => {
      flashText = message;
      flashUntil = performance.now() + 1500;
    };
    try {
      const promise = navigator.clipboard?.writeText(text);
      if (promise) {
        void promise.then(
          () => flash('COPIED'),
          () => {
            window.prompt('Telemetry — copy this:', text);
            flash('COPIED (prompt)');
          }
        );
        return;
      }
      window.prompt('Telemetry — copy this:', text);
      flash('COPIED (prompt)');
    } catch {
      try {
        window.prompt('Telemetry — copy this:', text);
        flash('COPIED (prompt)');
      } catch {
        flash('COPY FAILED');
      }
    }
  }

  function show(): void {
    if (root) return;
    root = document.createElement('div');
    root.className = 'rr-telemetry';
    root.setAttribute('aria-hidden', 'true');
    mainEl = document.createElement('pre');
    mainEl.className = 'rr-telemetry-main';
    logEl = document.createElement('div');
    logEl.className = 'rr-telemetry-log';
    root.append(mainEl, logEl);
    document.body.appendChild(root);
    paintedLogRevision = -1;

    // Capture phase, passive, no preventDefault: the engine's own SDL handlers
    // must see every one of these unchanged.
    const listen = { capture: true, passive: true } as const;
    window.addEventListener('keydown', onKeyDown, listen);
    window.addEventListener('keyup', onKeyUp, listen);
    window.addEventListener('blur', onBlur);
    for (const type of ['pointermove', 'pointerdown', 'pointerup', 'pointercancel', 'mousedown', 'mouseup']) {
      window.addEventListener(type, onPointer as EventListener, listen);
    }
    rectAt = 0;
    fpsWindowAt = performance.now();
    frames = 0;
    raf = requestAnimationFrame(tick);
  }

  function hide(): void {
    if (!root) return;
    cancelAnimationFrame(raf);
    raf = 0;
    const listen = { capture: true } as const;
    window.removeEventListener('keydown', onKeyDown, listen);
    window.removeEventListener('keyup', onKeyUp, listen);
    window.removeEventListener('blur', onBlur);
    for (const type of ['pointermove', 'pointerdown', 'pointerup', 'pointercancel', 'mousedown', 'mouseup']) {
      window.removeEventListener(type, onPointer as EventListener, listen);
    }
    root.remove();
    root = null;
    mainEl = null;
    logEl = null;
    held.clear();
  }

  function toggle(next?: boolean): void {
    const want = next ?? !on;
    if (want === on) return;
    on = want;
    persist(on);
    if (on) show();
    else hide();
  }

  // The hotkeys are live whether or not the panel is: the toggle is how it
  // gets turned on mid-session, which is the only way to catch a bug already
  // in progress. Not F-keys: macOS eats F9/F10 without Fn, and F10 is the
  // engines' own quit key. Backquote is bound by neither game nor browser;
  // Ctrl+Shift+Y is free in Chrome and both engines (Ctrl+Y is unbound there).
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.code === 'Backquote' && !e.ctrlKey && !e.metaKey && !e.altKey) toggle();
      else if (e.code === 'KeyY' && e.ctrlKey && e.shiftKey && on) copy();
    },
    { capture: true, passive: true }
  );

  if (readFlag()) toggle(true);

  return {
    engineLog(line: string): void {
      logLines.push(line);
      if (logLines.length > LOG_KEEP) logLines.shift();
      logRevision++;
    },
    setEngine(info: EngineInfo): void {
      engineInfo = { ...engineInfo, ...info };
    },
    isOn: () => on,
    toggle,
  };
}
